import { createSlice, nanoid } from "@reduxjs/toolkit";
import { PASSENGER } from "src/constants";

const MAX_TRAVELERS = 9;

const emptyTraveler = () => ({
  id: nanoid(),
  name: "",
  surname: "",
  nationality: "",
  gender: "",
  dob: "",
  ageGroup: PASSENGER.adult,
});

const initialState = {
  tour: null,
  contact: { email: "", tel: "" },
  travelers: [],
  // Set once /booking/create succeeds (now fired as soon as the Reservation page loads, not
  // just on submit — see TourReservation.jsx) — the Checkout page's payment call needs this
  // reference. Null means "not booked yet" / "booking failed".
  correlationId: null,
  bookingNumber: null,
  // Traveler ids the backend already has an add_passenger for, kept here (not just in a
  // component ref) so a page remount for the same tour doesn't forget and re-send add_passenger
  // for someone already added. See getTourAutosaveMessages/getTourBatchUpdateMessages.
  syncedTravelerIds: [],
};

const tourBookingSlice = createSlice({
  name: "tourBooking",
  initialState,
  reducers: {
    setTour(state, action) {
      const tour = action.payload;
      if (state.tour?.code !== tour.code) {
        // Switching to a different tour mid-flow (e.g. the user abandoned one reservation and
        // started another) — the previous tour's correlationId/travelers/contact must not carry
        // over, or TourReservation's autosave would silently reuse a stale booking that belongs
        // to a different tour.
        state.correlationId = null;
        state.bookingNumber = null;
        state.contact = { email: "", tel: "" };
        state.travelers = [];
        state.syncedTravelerIds = [];
      }
      state.tour = tour;
      if (state.travelers.length === 0) {
        state.travelers.push(emptyTraveler());
      }
    },
    updateContact(state, action) {
      state.contact = { ...state.contact, ...action.payload };
    },
    setTravelerCount(state, action) {
      const count = Math.max(1, Math.min(MAX_TRAVELERS, action.payload));
      if (count > state.travelers.length) {
        while (state.travelers.length < count) {
          state.travelers.push(emptyTraveler());
        }
      } else {
        state.travelers.length = count;
      }
    },
    updateTraveler(state, action) {
      const { id, values } = action.payload;
      const traveler = state.travelers.find((t) => t.id === id);
      if (traveler) Object.assign(traveler, values);
    },
    setBookingReference(state, action) {
      state.correlationId = action.payload.correlationId;
      state.bookingNumber = action.payload.bookingNumber;
    },
    setSyncedTravelerIds(state, action) {
      state.syncedTravelerIds = action.payload;
    },
    resetTourBooking() {
      return initialState;
    },
  },
});

export const tourBookingActions = tourBookingSlice.actions;
export const MAX_TOUR_TRAVELERS = MAX_TRAVELERS;
export const selectTourBooking = (state) => state.tourBooking;
export default tourBookingSlice.reducer;
